// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Shipment } from '@/shipment/api/types';

/**
 * Quantities calculated from shipments for a specific item.
 */
interface ShipmentQuantities {
  pending: number;
  shipped: number;
  delivered: number;
  received: number;
}

/**
 * Calculates pending, shipped, delivered, and received quantities for an item
 * by examining the related shipments.
 *
 * Cancelled shipments are excluded from all calculations.
 *
 * - Pending: shipments with no shippedAt
 * - Shipped: shipments with shippedAt but no deliveredAt
 * - Delivered: shipments with deliveredAt but no receivedAt
 * - Received: shipments with receivedAt date
 */
export function calculateShipmentQuantities(
  shipments: Shipment[],
  itemId?: string
): ShipmentQuantities {
  let pending = 0;
  let shipped = 0;
  let delivered = 0;
  let received = 0;

  shipments.forEach((shipment) => {
    // Skip cancelled shipments - their quantities shouldn't be counted
    if (shipment.cancelledAt) return;

    shipment.items.forEach((lineItem) => {
      const quantity = lineItem.quantity ?? 0;
      const receivedQuantity = lineItem.receivedQuantity ?? 0;

      // Match items by catalog item ID (nested under item.item)
      const isMatch = itemId && lineItem.item.id === itemId;

      if (!isMatch) return;

      if (shipment.receivedAt) {
        received += receivedQuantity;
      } else if (shipment.deliveredAt) {
        delivered += quantity;
      } else if (shipment.shippedAt) {
        shipped += quantity;
      } else {
        pending += quantity;
      }
    });
  });

  return { pending, shipped, delivered, received };
}
